/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <Teasers {...cmsData} />)
 * and then make these props fit the final module.
 */
import React from "react"
import { Section, CmsImage } from "@bw/bits"
import { Teasers as TeasersModule } from "@bw/modules"
import { Teaser } from "@bw/partials"
import { useStaticQuery, graphql } from "gatsby"

const TeasersManual = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  related,
  numberOfColumns,
  anchor,
}) => {
  const data = useStaticQuery(graphql`
    query allItemsInCollection {
      posts: allCollectionArticles {
        nodes {
          statamicId
          title
          slug
          excerpt: teaserExcerpt
          image: teaserImage {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 350)
              }
            }
          }
        }
      }
      pages: allCollectionPages {
        nodes {
          statamicId
          title
          slug
          excerpt: teaserExcerpt
          image: teaserImage {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 350)
              }
            }
          }
        }
      }
      prestations: allCollectionPrestations {
        nodes {
          statamicId
          title
          slug
          excerpt: teaserExcerpt
          image: teaserImage {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 350)
              }
            }
          }
        }
      }
      jobs: allCollectionJobs {
        nodes {
          slug
          title
          occupancyRate
          pharmacy {
            title
          }
          image {
            url
          }
        }
      }
      news: allCollectionNews(sort: { date: DESC }) {
        nodes {
          statamicId
          title
          slug
          date
          excerpt: teaserExcerpt
          image: teaserImage {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 350)
              }
            }
          }
          internal {
            type
          }
        }
      }
      magazines: allCollectionMagazines {
        nodes {
          slug
          publishDate
          statamicId
          title
          pdf {
            url
          }
          cover {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 329, height: 428)
              }
            }
          }
        }
      }
      events: allCollectionEvents {
        nodes {
          statamicId
          title
          slug
          excerpt: teaserExcerpt
          image: teaserImage {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 350)
              }
            }
          }
        }
      }
    }
  `)
  let nodes = [
    ...data.pages.nodes,
    ...data.posts.nodes,
    ...data.news.nodes,
    ...data.prestations.nodes,
    ...data.jobs.nodes,
    ...data.magazines.nodes,
    ...data.events.nodes,
  ]

  const items = related
    .map(item => {
      return nodes.find(node => node.statamicId === item.id)
    })
    .filter(x => !!x)

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition: buttonPosition?.value,
        anchor,
      }}
    >
      <TeasersModule numberOfColumns={numberOfColumns?.value}>
        {items.map(({ title, excerpt, image, slug }, i) => {
          return (
            <Teaser
              key={i}
              {...{ title, excerpt }}
              image={
                <CmsImage
                  src={image || "/placeholder-teaser.jpg"}
                  alt={title}
                />
              }
              to={slug}
            />
          )
        })}
      </TeasersModule>
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on TeasersManualModule {
 *   ...TeasersManualModuleFragment
 * }
 *
 * This is also what you will get in the props of your {{pascalCase name}}
 * resolver component.
 */
export const query = graphql`
  fragment TeasersManualModuleFragment on TeasersManualModule {
    related {
      id
    }
    numberOfColumns {
      value
    }
  }
`

export { TeasersManual }
